// @ts-check
/** @typedef {import('../types/api').Task} Task */
/** @typedef {import('../types/api').TaskAnalytics} TaskAnalytics */
/** @typedef {import('../types/api').AdminUser} AdminUser */

import { AlertCircle, Calendar, CheckCircle2, Clock, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import TaskCard from '../components/tasks/TaskCard';
import TaskCreateModal from '../components/tasks/TaskCreateModal';
import TaskDetailModal from '../components/tasks/TaskDetailModal';
import TaskFilterSidebar from '../components/tasks/TaskFilterSidebar';
import BulkActionBar from '../components/ui/BulkActionBar';
import BulkConfirmModal from '../components/ui/BulkConfirmModal';
import { HeaderCheckbox } from '../components/ui/RowCheckbox';
import { useToast } from '../context/ToastContext';
import useRowSelection from '../hooks/useRowSelection';
import { reportBulkResult, runBulk } from '../services/bulkService';
import taskService from '../services/taskService';
import api from '../utils/apiClient';

const BULK_STATUSES = [
  ['to_do', 'To Do'],
  ['in_progress', 'In Progress'],
  ['review', 'Review'],
  ['done', 'Done'],
  ['blocked', 'Blocked'],
];

const BULK_SELECT_CLASS = 'px-2 py-1.5 text-xs rounded-md bg-white/10 text-white border-0 disabled:opacity-50';

const TasksPage = () => {
  const toast = useToast();
  /** @type {[Task[], Function]} */
  const [tasks, setTasks] = useState([]);
  /** @type {[TaskAnalytics | null, Function]} */
  const [analytics, setAnalytics] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'my_tasks', 'completed'
  /** @type {[Record<string, any>, Function]} */
  const [filters, setFilters] = useState({});
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const taskFilters = { ...filters };

      // Apply tab-specific filters
      if (activeTab === 'my_tasks' && currentUser) {
        taskFilters.assigned_to = currentUser.id;
      } else if (activeTab === 'completed') {
        taskFilters.status = 'done';
      }

      const data = await taskService.getTasks(taskFilters);
      setTasks(data);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters, currentUser]);

  const loadData = useCallback(async () => {
    await Promise.all([loadTasks(), loadAnalytics()]);
  }, [loadTasks]);

  useEffect(() => {
    // Get current user from localStorage
    const userData = localStorage.getItem('adminUser');
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }

    loadData();
    loadAdminUsers();
  }, [loadData]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const loadAnalytics = async () => {
    try {
      const data = await taskService.getAnalytics();
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadAdminUsers = async () => {
    try {
      const data = await api.get('/api/admin/users?limit=200');
      setAdminUsers(data.items);
    } catch (error) {
      console.error('Error loading admin users:', error);
      setAdminUsers([]);
    }
  };

  const selection = useRowSelection(
    tasks.map((t) => t.id),
    { resetKey: `${activeTab}|${JSON.stringify(filters)}` },
  );

  const handleBulk = async (action, params, verb) => {
    setBulkBusy(true);
    try {
      const result = await runBulk('/api/admin/tasks/bulk', { ids: selection.selectedIds, action, params });
      reportBulkResult(toast, result, verb, 'task');
      selection.clear();
      setConfirmBulkDelete(false);
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to update tasks');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleResetFilters = () => {
    setFilters({});
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
  };

  const handleTaskUpdate = async () => {
    await loadData();
    if (selectedTask) {
      // Reload selected task details
      const updated = await taskService.getTask(selectedTask.id);
      setSelectedTask(updated);
    }
  };

  return (
    <div className="min-h-screen bg-muted/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Tasks</h1>
          <p className="mt-2 text-sm text-muted-foreground">Manage your team's tasks and workflows</p>
        </div>

        {/* Stats Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-muted-foreground">Due Today</p>
                  <p className="text-2xl font-bold text-foreground">{analytics.due_today}</p>
                </div>
                <Calendar className="text-blue-500" size={32} />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-muted-foreground">Overdue</p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">{analytics.overdue}</p>
                </div>
                <AlertCircle className="text-red-500" size={32} />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-muted-foreground">In Progress</p>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {analytics.status_counts?.in_progress || 0}
                  </p>
                </div>
                <Clock className="text-blue-500" size={32} />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-muted-foreground">Completed (Week)</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {analytics.completed_this_week}
                  </p>
                </div>
                <CheckCircle2 className="text-green-500" size={32} />
              </div>
            </div>
          </div>
        )}

        {/* Action Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          {/* Tabs */}
          <div className="flex space-x-1 bg-card border border-border rounded-lg p-1 overflow-x-auto">
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'all'
                  ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              All Tasks
            </button>
            <button
              onClick={() => setActiveTab('my_tasks')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'my_tasks'
                  ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              My Tasks
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'completed'
                  ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Completed
            </button>
          </div>

          {/* Create Button */}
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} />
            Create Task
          </button>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Filters */}
          <div className="lg:col-span-1">
            <TaskFilterSidebar
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
              adminUsers={adminUsers}
            />
          </div>

          {/* Tasks List */}
          <div className="lg:col-span-3">
            <BulkActionBar count={selection.count} onClear={selection.clear}>
              <select
                aria-label="Set status"
                value=""
                disabled={bulkBusy}
                onChange={(e) => handleBulk('set_status', { status: e.target.value }, 'updated')}
                className={BULK_SELECT_CLASS}
              >
                <option value="">Set status...</option>
                {BULK_STATUSES.map(([value, label]) => (
                  <option key={value} value={value} className="text-foreground">
                    {label}
                  </option>
                ))}
              </select>
              <select
                aria-label="Assign to"
                value=""
                disabled={bulkBusy}
                onChange={(e) =>
                  handleBulk('assign', { assigned_to: e.target.value === 'none' ? null : e.target.value }, 'assigned')
                }
                className={BULK_SELECT_CLASS}
              >
                <option value="">Assign to...</option>
                <option value="none" className="text-foreground">
                  Unassigned
                </option>
                {adminUsers.map((user) => (
                  <option key={user.id} value={user.id} className="text-foreground">
                    {user.full_name}
                  </option>
                ))}
              </select>
              <BulkActionBar.Button tone="danger" onClick={() => setConfirmBulkDelete(true)} disabled={bulkBusy}>
                Delete
              </BulkActionBar.Button>
            </BulkActionBar>
            {!loading && tasks.length > 0 && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                <HeaderCheckbox selection={selection} label="Select all tasks" />
                Select all
              </label>
            )}
            {loading ? (
              <LoadingScreen />
            ) : tasks.length === 0 ? (
              <div className="text-center py-12 bg-card border border-border rounded-lg">
                <CheckCircle2 size={48} className="mx-auto text-muted-foreground/70 mb-4" />
                <p className="text-muted-foreground mb-2">No tasks found</p>
                <p className="text-sm text-muted-foreground mb-4">
                  {activeTab === 'my_tasks'
                    ? "You don't have any tasks assigned yet"
                    : 'Create your first task to get started'}
                </p>
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  <Plus size={16} />
                  Create Task
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {tasks.map((task) => (
                  <TaskCard key={task.id} task={task} onClick={handleTaskClick} selection={selection} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {confirmBulkDelete && (
        <BulkConfirmModal
          title="Delete tasks"
          message="Deleted tasks are hidden from every list."
          count={selection.count}
          confirmLabel="Delete"
          danger
          requireCount
          busy={bulkBusy}
          onConfirm={() => handleBulk('delete', {}, 'deleted')}
          onClose={() => setConfirmBulkDelete(false)}
        />
      )}
      <TaskCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={loadData}
        adminUsers={adminUsers}
        currentUser={currentUser}
      />

      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onUpdate={handleTaskUpdate}
          adminUsers={adminUsers}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default TasksPage;
